import React, { useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, Cell,
} from 'recharts';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import {
  useAimSnapshot,
  useAimCitationSources, useAimAmenityGap,
} from '../lib/aimQueries';
import { isPending } from '../lib/queryState';
import { bucketFor } from '../lib/ratingScale';
import AimEmpty, { AimLoading, AimError } from '../primitives/AimEmpty';
import InsightCard from '../primitives/InsightCard';
import InfoTooltip from '../primitives/InfoTooltip';
import PeriodLabel from '../primitives/PeriodLabel';
import { buildLeakageInsight, buildCitationInsight, buildAmenityGapInsight, buildAmenityUnderrepNarrative } from '../lib/insights';
import { prettifyAmenityLabel } from '../lib/categoryLabel';
import {
  amenityPriorityRows, aiMarkerOrder, aiGapStatus, hasClientPriorities,
} from '../lib/amenityGap';
import { prettifyProviderLabel } from '../lib/providerColors';
import {
  classifyCitation,
  CITATION_TYPE_LABEL,
  CITATION_TYPE_CLASS,
} from '../lib/citationType';
import { summarizeLeakageBreakdown } from '../lib/leakageIndex';
import usePagedRows from '../lib/usePagedRows';
import AimPagination from '../primitives/AimPagination';
import { TABLE_FRAME_FLUSH, TABLE_THEAD, TABLE_ROW } from '../../shared/tableStyles';

/* ── 5A Citation Source Mapping ── */
// fn_get_aim_citation_sources defaults p_limit to 25, which is one page — so
// paging over it would never have shown a control. Verde's pinned run has 114
// sources, so ask for the whole set and page through it client-side, the way
// every other AIM table does. The RPC caps at whatever exists.
const CITATION_SOURCE_FETCH_LIMIT = 500;

function CitationSources({ clientId, runId }) {
  const { data: rows, loading } = useAimCitationSources(clientId, runId, CITATION_SOURCE_FETCH_LIMIT);
  const list = Array.isArray(rows) ? rows : [];
  const { visibleRows, page, setPage, pageCount, totalItems } = usePagedRows(list, 25);
  // The insight is built from the full run, not the visible page — it reports on
  // the citation mix as a whole, so paging must not change what it says.
  const insight = buildCitationInsight(list.map((r) => ({
    source: r.cited_domain,
    is_own_site: r.is_client_site,
    citation_count: r.citations,
  })));
  if (loading) return <AimLoading height={160} />;
  if (list.length === 0) return <AimEmpty message="No citation source data for this run." />;
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Citation Sources</h3>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            Domains AI most often cites when answering questions about your property. Use it to spot which third parties (OTAs, review sites, directories) shape your AI presence.
          </p>
        </div>
        <div className={`${TABLE_FRAME_FLUSH} responsive-card-table-wrapper`}>
        <table className="w-full text-sm text-gray-900 dark:text-gray-100 min-w-[480px] responsive-card-table">
          <thead className={TABLE_THEAD}>
            <tr>
              <th className="text-left px-4 py-2.5 whitespace-nowrap">Source</th>
              <th className="text-right px-4 py-2.5 whitespace-nowrap">Citations</th>
              <th className="text-right px-4 py-2.5 whitespace-nowrap">Providers</th>
              <th className="text-left px-4 py-2.5 whitespace-nowrap">Type</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r, i) => {
              const isOwn = !!r.is_client_site;
              const bucket = classifyCitation({
                domain: r.cited_domain,
                is_client_site: isOwn,
              });
              return (
                <tr key={`${r.cited_domain}-${i}`} className={`${TABLE_ROW} ${isOwn ? '!bg-emerald-50 dark:!bg-emerald-950/30' : ''}`}>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white" data-label="Source">
                    <span className="inline-flex items-center gap-1">
                      {isOwn && <span>⭐</span>}
                      <span>{r.cited_domain || '—'}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums" data-label="Citations">{r.citations}</td>
                  <td className="px-4 py-3 text-right tabular-nums" data-label="Providers">{r.from_providers}</td>
                  <td className={`px-4 py-2 text-xs ${CITATION_TYPE_CLASS[bucket]}`} data-label="Type">
                    {CITATION_TYPE_LABEL[bucket]}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        <div className="px-4 pb-3">
          <AimPagination page={page} pageCount={pageCount} onPage={setPage} totalItems={totalItems} label="source" />
        </div>
      </div>
      {insight && <InsightCard {...insight} />}
    </div>
  );
}

function fmtDirectionRaw(raw) {
  if (!raw) return '—';
  return String(raw)
    .split('|')
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) return trimmed;
      if (trimmed.toLowerCase() === 'ota') return 'OTA';
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    })
    .join('|');
}

/* ── 5B Booking Direction Leakage ── */
// The headline, the callout, the segment counts and the table all come from ONE P5 snapshot: the
// backend computes `value_numeric` and `value_breakdown` together, so they cannot disagree.
function BookingLeakage({ clientId, runId }) {
  const snapQuery = useAimSnapshot(clientId, 'P5', '', runId);
  const { data: snap, error, refresh } = snapQuery;
  if (isPending(snapQuery)) return <AimLoading height={200} />;
  if (error) return <AimError message="Booking direction data unavailable." onRetry={refresh} />;
  if (!snap) return <AimEmpty message="P5 leakage snapshot not available." />;
  // A missing figure is "no data", never 0%: a silent 0% reads as "AI never sends guests to an OTA".
  const leakageIndex = snap.value_numeric == null ? NaN : Number(snap.value_numeric);
  if (!Number.isFinite(leakageIndex)) {
    return <AimEmpty message="No booking-direction leakage figure for this run." />;
  }

  const dist = Array.isArray(snap.value_breakdown) ? snap.value_breakdown : [];
  const summary = summarizeLeakageBreakdown(dist);
  // The callout states the SAME figure as the headline.
  const insight = buildLeakageInsight({ value_numeric: leakageIndex });

  let cardColor = '#22C55E';
  if (leakageIndex > 50) cardColor = '#EF4444';
  else if (leakageIndex > 20) cardColor = '#F59E0B';

  const segments = [
    { key: 'direct', label: 'Direct', color: '#10B981', count: summary.direct },
    { key: 'leakage', label: 'Leakage', color: '#EF4444', count: summary.leakage },
    { key: 'ambiguous', label: 'Ambiguous', color: '#9CA3AF', count: summary.ambiguous },
  ];
  // Compute total from the rendered segments only — RPC rows with unrecognized
  // `direction_class` values would otherwise inflate the divisor and leave the
  // bar visibly short on the right.
  const total = segments.reduce((s, seg) => s + seg.count, 0) || 1;

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-baseline justify-between">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {'Booking Direction Leakage ('}
              <InfoTooltip
                label="P5 — Booking Direction Leakage"
                trigger={<span className="border-b border-dotted border-gray-400 dark:border-gray-500 cursor-help">P5</span>}
              >
                <strong>P5 — Booking Direction Leakage.</strong> The share of booking directions that point to a
                third-party OTA instead of your direct channels (website, call, email), among the mentions
                that point anywhere classifiable. A mention that points to both your own site and an OTA
                counts as half. Ambiguous mentions are excluded. The bar below draws the raw count per
                class, so it will not reproduce the headline exactly when mixed mentions exist. Color
                thresholds: green &lt; 20%, yellow 20–50%, red &gt; 50%.
              </InfoTooltip>
              {')'}
            </h3>
          </div>
          <span className="text-3xl font-bold tabular-nums" style={{ color: cardColor }}>{leakageIndex.toFixed(1)}%</span>
        </div>
        <PeriodLabel snapshot={snap} />
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          When AI tells searchers where to book, this percent of the time it points them to a third party instead of you.
        </p>
        <div className="mt-3 flex w-full h-4 rounded overflow-hidden">
          {segments.map((s) => {
            const pct = (s.count / total) * 100;
            return <div key={s.key} title={`${s.label}: ${s.count}`} style={{ width: `${pct}%`, backgroundColor: s.color }} />;
          })}
        </div>
        <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
          {segments.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} /> {s.label}: {s.count.toLocaleString('en-US')}
            </span>
          ))}
        </div>
      </div>

      {dist.length > 0 && (() => {
        const CLASS_ORDER = ['direct', 'leakage', 'ambiguous'];
        const CLASS_LABELS = { direct: 'Direct', leakage: 'Leakage', ambiguous: 'Ambiguous' };
        const groups = dist.reduce((acc, r) => {
          const cls = (r.direction_class || 'ambiguous').toLowerCase();
          (acc[cls] = acc[cls] || []).push(r);
          return acc;
        }, {});
        Object.values(groups).forEach((g) => g.sort((a, b) =>
          Number(b.mention_count ?? b.count ?? 0) - Number(a.mention_count ?? a.count ?? 0)));
        return (
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
            <div className={`${TABLE_FRAME_FLUSH} responsive-card-table-wrapper`}>
            <table className="responsive-card-table w-full text-sm text-gray-900 dark:text-gray-100 min-w-[480px]">
              <thead className={TABLE_THEAD}>
                <tr>
                  <th className="text-left px-4 py-2.5 whitespace-nowrap">Direction (raw)</th>
                  <th className="text-left px-4 py-2.5 whitespace-nowrap">Class</th>
                  <th className="text-right px-4 py-2.5 whitespace-nowrap">Count</th>
                </tr>
              </thead>
              {CLASS_ORDER.filter((k) => groups[k]?.length).map((k) => {
                const bandStyle = {
                  direct: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
                  leakage: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300',
                  ambiguous: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
                }[k] || 'bg-gray-50 dark:bg-gray-800/40 text-gray-500 dark:text-gray-400';
                return (
                <tbody key={k}>
                  <tr className={bandStyle}>
                    <td colSpan={3} className="px-4 py-1.5 text-xs uppercase tracking-wide font-semibold">
                      {CLASS_LABELS[k]}
                    </td>
                  </tr>
                  {groups[k].slice(0, 50).map((r, i) => (
                    <tr key={`${k}-${i}`} className={TABLE_ROW}>
                      <td data-label="Direction (raw)" className="px-4 py-3 truncate max-w-[420px]" title={r.direction_raw}>{fmtDirectionRaw(r.direction_raw)}</td>
                      <td data-label="Class" className="px-4 py-3 capitalize">{r.direction_class || '—'}</td>
                      <td data-label="Count" className="px-4 py-3 text-right tabular-nums">{Number(r.mention_count ?? r.count ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                );
              })}
            </table>
            </div>
          </div>
        );
      })()}

      {insight && <InsightCard {...insight} />}
    </div>
  );
}

/* ── 5C Amenity Gap ── */
function AmenityGap({ clientId, runId }) {
  const { data, loading } = useAimAmenityGap(clientId, runId);
  const aiRows = Array.isArray(data?.rows) ? data.rows : [];
  // The RPC sends bare amenity keys rather than {amenity, gap, rank} objects, so
  // normalize before render. `ranked` is false under that shape because the keys
  // arrive alphabetical, not priority-ordered — see lib/amenityGap.
  const { rows: priorities, ranked } = amenityPriorityRows(data?.client_priorities, aiRows);
  const prioritiesSet = hasClientPriorities(data) && priorities.length > 0;
  const missingPriorities = priorities.filter((p) => !p.matched);
  const insight = buildAmenityGapInsight(missingPriorities.map((p) => ({ amenity: p.amenity, gap: 'missing' })));
  if (loading) return <AimLoading height={160} />;
  if (aiRows.length === 0 && priorities.length === 0) return <AimEmpty message="No amenity data for this run." />;
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-gray-900 dark:text-gray-100">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Amenity Identity Gap</h3>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-2">
          How AI&apos;s view of your amenities lines up with the priorities you&apos;ve set. Items marked ✓ are where AI and your priority list agree; ✗ flags a gap to close in your content.
        </p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-xs text-gray-600 dark:text-gray-400">
          <span className="inline-flex items-center gap-1 font-semibold">
            <span aria-hidden="true" className="text-emerald-600 dark:text-emerald-400 text-xl leading-none">✓</span>
            AI mentions this in line with your priorities
          </span>
          <span className="inline-flex items-center gap-1 font-semibold">
            <span aria-hidden="true" className="text-red-600 dark:text-red-400 text-xl leading-none">✗</span>
            AI under-emphasizes or doesn&rsquo;t mention this
          </span>
          <span className="inline-flex items-center gap-1 font-semibold">
            <span aria-hidden="true" className="text-gray-400 dark:text-gray-500 text-xl leading-none">~</span>
            AI mentions this but it&rsquo;s not on your priority list
          </span>
        </div>
        <div className="grid grid-cols-2 gap-x-6 text-sm max-[499px]:grid-cols-1 max-[499px]:gap-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">What AI thinks</div>
            <ol className="space-y-1">
              {[...aiRows.slice(0, 10)]
                .sort((a, b) => aiMarkerOrder(a) - aiMarkerOrder(b))
                .map((r, i) => {
                const status = aiGapStatus(r);
                const isUnder = status === 'underrepresented';
                const isAligned = status === 'aligned';
                const rowColor = isUnder
                  ? 'text-red-600 dark:text-red-400 font-medium'
                  : isAligned
                  ? 'text-emerald-600 dark:text-emerald-400 font-medium'
                  : '';
                return (
                  <li key={r.ai_amenity} className={`flex items-center gap-2 ${rowColor}`}>
                    {/* The AI's own rank, not the row position — the list is
                        grouped by marker, so an index would misreport it. */}
                    <span className="tabular-nums text-xs text-gray-400 w-6">{(r.ai_rank ?? i + 1)}.</span>
                    {isAligned ? (
                      <span className="text-emerald-600 dark:text-emerald-400 text-xl leading-none" aria-label="aligned">✓</span>
                    ) : isUnder ? (
                      <span className="text-red-600 dark:text-red-400 text-xl leading-none" aria-label="underrepresented">✗</span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 text-xl leading-none" aria-label="not on priority list">~</span>
                    )}
                    <span className="flex-1">{prettifyAmenityLabel(r.ai_amenity)}</span>
                    {r.confidence === 'single_observation' && (
                      <span
                        className="text-[9px] uppercase tracking-wide font-semibold px-1 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                        title="Single observation — appeared in just one AI response"
                      >
                        1×
                      </span>
                    )}
                    <span className="text-xs text-gray-500">{r.mentions}×</span>
                  </li>
                );
              })}
            </ol>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">What you want</div>
            {!prioritiesSet && (
              // Nothing to compare against: without a priority list the ✓/✗
              // markers would read as agreement with a list nobody has set.
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                You haven&apos;t set any amenity priorities yet, so there&apos;s nothing to compare
                AI&apos;s view against. The list on the left is what AI currently emphasizes — it
                isn&apos;t a verdict on your priorities.
              </p>
            )}
            {prioritiesSet && !ranked && (
              // Say so rather than numbering the list by array position: the RPC
              // sends these alphabetically, so any number here would invent a
              // priority order. Grouped by marker instead, like the AI column.
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1.5">
                Your priority order isn&apos;t available from the backend for this run, so these
                aren&apos;t ranked — they&apos;re grouped by status.
              </p>
            )}
            <ol className="space-y-1">
              {prioritiesSet && priorities.map((p) => (
                <li key={p.amenity} className="flex items-center gap-2">
                  {ranked && (
                    <span className="tabular-nums text-xs text-gray-400 w-6">{p.rank}.</span>
                  )}
                  {p.matched
                    ? <span aria-label="matched by AI" className="text-emerald-600 dark:text-emerald-400 text-xl leading-none">✓</span>
                    : <span aria-label="not mentioned by AI" className="text-red-600 dark:text-red-400 text-xl leading-none">✗</span>}
                  <span>{prettifyAmenityLabel(p.amenity)}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
        {(() => {
          const under = aiRows
            .filter((r) => aiGapStatus(r) === 'underrepresented')
            .sort((a, b) => (a.client_rank ?? 99) - (b.client_rank ?? 99));
          if (under.length === 0) return null;
          const parts = under.slice(0, 2).map((r) => `${prettifyAmenityLabel(r.ai_amenity)} (your #${r.client_rank}, AI rank #${r.ai_rank})`);
          const narrative = buildAmenityUnderrepNarrative(parts);
          if (!narrative) return null;
          return (
            <p className="mt-3 text-xs text-red-600 dark:text-red-400">{narrative}</p>
          );
        })()}
      </div>
      {insight && <InsightCard {...insight} />}
    </div>
  );
}

/* ── 5D Citation-to-Accuracy (P14, NULL-filtered at source) ── */
function CitationAccuracy({ clientId, runId }) {
  const snapQuery = useAimSnapshot(clientId, 'P14', '', runId);
  const { data: snap, error, refresh } = snapQuery;
  const rows = useMemo(() => {
    const b = snap?.value_breakdown;
    return Array.isArray(b) ? b : Array.isArray(b?.providers) ? b.providers : [];
  }, [snap]);

  if (isPending(snapQuery)) return <AimLoading height={220} />;
  if (error) return <AimError message="Citation-accuracy data unavailable." onRetry={refresh} />;
  if (rows.length === 0) {
    return <AimEmpty message="P14 citation-accuracy snapshot not available yet." />;
  }
  // Spec excludes SerpAPI providers from citation→accuracy because they don't
  // produce checkable facts. Their accuracy comes back null from the scorecard
  // — drop those rows entirely rather than coercing to 0% bars.
  const data = rows
    .filter((r) => (r.avg_accuracy_pct ?? r.accuracy_pct) != null)
    .map((r) => ({
      name: prettifyProviderLabel(r.provider_name || r.provider),
      avg: Number(r.avg_accuracy_pct ?? r.accuracy_pct),
      high: Number(r.high_citation_accuracy ?? 0),
      low: Number(r.low_citation_accuracy ?? 0),
    }));

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="flex items-center gap-1.5 mb-1">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {'Citation → Accuracy ('}
          <InfoTooltip
            label="P14 — Citation to Accuracy"
            trigger={<span className="border-b border-dotted border-gray-400 dark:border-gray-500 cursor-help">P14</span>}
          >
            <strong>P14 — Citation to Accuracy.</strong> Does citing your own site make AI more accurate about you? Compares
            each provider's accuracy on responses that cite your site vs. responses
            that don't. Search-result providers (Google AI Overviews, AI Mode, Organic Search) are
            excluded because they return rankings and summaries, not checkable
            facts.
          </InfoTooltip>
          {')'}
        </h3>
      </div>
      <div className="mb-2"><PeriodLabel snapshot={snap} /></div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Accuracy measured only for providers that make factual claims. Search-result providers are excluded because they return rankings and summaries, not checkable facts.
      </p>
      <ResponsiveContainer width="100%" height={Math.max(200, data.length * 32)}>
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
          <CartesianGrid stroke="rgba(148,163,184,0.2)" />
          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
          <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
          <ThemedChartTooltip />
          <Bar dataKey="avg" name="Avg accuracy" radius={[0, 4, 4, 0]}>
            {data.map((d) => <Cell key={d.name} fill={bucketFor(d.avg).hex} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-4">
        <div className="flex items-center gap-1.5 mb-1">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">High vs Low citation accuracy</h4>
          <InfoTooltip label="High vs Low citation accuracy">
            Splits each provider's responses into ones with many citations
            (high) and few citations (low), then measures accuracy on each
            half. A wide gap means the provider leans on citations to get
            facts right; a narrow gap means accuracy holds up either way.
          </InfoTooltip>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Accuracy on responses that include many citations vs few. A wide gap suggests the provider depends on citations for accuracy.
        </p>
        <ResponsiveContainer width="100%" height={Math.max(200, data.length * 28)}>
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="rgba(148,163,184,0.2)" />
            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
            <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
            <ThemedChartTooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="high" name="High citations" fill="#10B981" />
            <Bar dataKey="low" name="Low citations" fill="#EF4444" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function ContentTab({ clientId, runId }) {
  return (
    <div className="space-y-5">
      <CitationSources clientId={clientId} runId={runId} />
      <BookingLeakage clientId={clientId} runId={runId} />
      <AmenityGap clientId={clientId} runId={runId} />
      <CitationAccuracy clientId={clientId} runId={runId} />
      {/* 5E Audience Alignment is intentionally hidden per v2 p.5 (P7 deferred). */}
    </div>
  );
}
